import './ui/base.css';
import { RealClock, TestClock, type Clock } from './core/clock';
import { createRng, DEFAULT_SEED, type Rng } from './core/rng';
import { ManualFrameSource, RafFrameSource, type FrameSource } from './core/loop';
import { detectCapabilities, detectStandalone } from './platform';
import { createApp, type App } from './app';
import { installHarness } from './harness';
import { LocalStore } from './store/localStore';
import { prefersReducedMotion } from './ui/a11y';
import { createRecognizer } from './speech/recognizer';
import { Speaker, detectSynth } from './speech/synth';
import { installOfflineShell, shouldRegister } from './offline/register';
import { SANDBOX_DB, sandboxEnabled, sandboxRequested, seedSandbox } from './dev/sandbox';
import { SERVED } from './content';

/**
 * Composition root. Everything injectable is chosen here and nowhere else: the clock, the PRNG,
 * the frame source, and platform capabilities. No module below this one constructs its own.
 */

const BUILD_ID = 'phase5-shell';

const params = new URLSearchParams(window.location.search);

/**
 * Harness mode is opt-in via `?harness=1`. In harness mode time and randomness are fully
 * controlled by the test; in normal mode the flag is absent and `window.__harness` is never
 * defined. See DECISIONS.md D-006 for why this is a URL flag rather than a separate build.
 */
const harnessMode = params.get('harness') === '1';
const seedParam = Number(params.get('seed'));
const initialSeed = Number.isFinite(seedParam) && params.has('seed') ? seedParam : DEFAULT_SEED;
const nowParam = Number(params.get('now'));
// A fixed default epoch so a harness run with no `now` is still reproducible.
const initialNow = Number.isFinite(nowParam) && params.has('now') ? nowParam : 1_700_000_000_000;

const root = document.getElementById('app');
if (!root) throw new Error('#app missing from index.html');

const capabilities = detectCapabilities(window);

/**
 * The effects canvas (T58). Absent in a document that does not have one, which is fine: nothing the
 * learner needs is drawn there.
 */
const fxCanvas = document.getElementById('fx') as HTMLCanvasElement | null;

/**
 * Read once at boot rather than watched.
 *
 * Someone who turns reduced motion on mid-ronda has a reason to, and the honest response is that the
 * next launch is still. Re-reading it per frame would mean a media-query lookup sixty times a second
 * for a setting that changes about never.
 */
const reducedMotion = prefersReducedMotion(window);

/**
 * The microphone, or null where speech cannot work here (T60).
 *
 * Built once at boot. Null in a browser with no `SpeechRecognition`, and — the case this app is
 * shaped around — in an installed iOS web app, where the API exists and never returns a result.
 */
const recognizer = createRecognizer(window, { standalone: detectStandalone(window) });

/**
 * The device's voice (T64). No audio files anywhere in this build.
 *
 * Built once and kept: voices arrive asynchronously on most platforms, and the `Speaker` listens for
 * `voiceschanged` so a learner who taps "hear it" a second after launch is not met with silence.
 */
const speaker = new Speaker({ synth: detectSynth(window) });

/**
 * The sandbox (`?sandbox=1`, development only): every tier open, on a fabricated history.
 *
 * Two flags have to agree, and one of them does not exist in a built bundle, so this cannot reach
 * the deployed app. See `src/dev/sandbox.ts` for why that matters more than it looks.
 */
const sandbox = sandboxEnabled({
  dev: import.meta.env.DEV,
  requested: sandboxRequested(params),
});

/**
 * The one persistence adapter (SPEC §10): IndexedDB, falling back to localStorage.
 *
 * Built once and shared across rebuilds, so `__harness.seed()` reseeding the PRNG does not also
 * throw away the learner's scheduling history — those are two unrelated things and a test that
 * conflated them would be asserting against a store that had silently emptied.
 *
 * The sandbox gets a database of its own. Not a key prefix, not a flag on the records — a separate
 * database, so a fabricated ladder cannot end up in the same place as a real review.
 */
const persistence = new LocalStore(sandbox ? { dbName: SANDBOX_DB } : {});

let clock: Clock;
let frames: FrameSource;
let rng: Rng = createRng(initialSeed);
let app: App;

/**
 * Layout violations throw in development and under the harness, and are reported but survivable in
 * a production build. The tests are where a misplaced control is meant to be caught; a white screen
 * at 3:40 a.m. over a layout nit would be the worse failure. See DECISIONS D-089.
 */
const strictLayout = import.meta.env.DEV || harnessMode;

function build(seed: number): App {
  rng = createRng(seed);
  return createApp({
    clock,
    rng,
    frames,
    root: root as HTMLElement,
    capabilities,
    buildId: BUILD_ID,
    strictLayout,
    store: persistence,
    fxCanvas,
    reducedMotion,
    recognizer,
    speaker,
  });
}

if (harnessMode) {
  const testClock = new TestClock(initialNow);
  const manualFrames = new ManualFrameSource();
  clock = testClock;
  frames = manualFrames;
  app = build(initialSeed);
  app.start();

  installHarness({
    clock: testClock,
    frames: manualFrames,
    ticks: () => app.loop.ticks(),
    reset: () => app.reset(),
    skipDemo: () => app.skipDemo(),
    reseed: (n: number) => {
      app.stop();
      app = build(n);
      app.start();
    },
    illegalTransitions: () => app.machine.rejected().map((r) => ({ ...r })),
  });
} else {
  clock = new RealClock();
  frames = new RafFrameSource();

  /*
   * The sandbox has to be seeded before the app loads the store, because `ItemLibrary.load` seeds a
   * fresh corpus itself if it finds one empty — and it would win the race, leaving a sandbox that
   * looks exactly like day one. Ordinary boot stays synchronous; only the flagged path waits.
   */
  const seeded = sandbox
    ? seedSandbox(persistence, SERVED, clock.now()).catch((error: unknown) => {
        // A sandbox that will not seed should say so and then open as an ordinary empty store,
        // rather than refusing to boot over a development convenience.
        console.error('sandbox seeding failed; opening an unseeded sandbox instead', error);
        return false;
      })
    : Promise.resolve(false);

  void seeded.then(() => {
    app = build(initialSeed);
    app.start();
  });
}

/**
 * The offline shell (T79), last and outside the branch above, because it must not run under the
 * harness — a cached page would make every e2e assertion a statement about a previous build.
 *
 * Registered after `app.start()`. The game opening is the thing that matters; caching it for next
 * time can wait the few milliseconds.
 */
if (
  shouldRegister({
    harness: harnessMode,
    production: import.meta.env.PROD,
    supported: 'serviceWorker' in navigator,
  })
) {
  void installOfflineShell(window);
}
